<template>
  <RedesignWebccPopupDialog v-bind="$attrs" ref="dialogRef">
    <template #button="{ toggle }">
      <RedesignWebccSelectButton
        icon="site/calendar"
        :text
        :placeholder="$t(`${trPrefix}placeholders.when`)"
        class="w-full md:w-72"
        @focus="toggle(true)"
        @clear="handleClear"
      />
    </template>

    <template #default="{ toggle }">
      <RedesignDateRangeSearchCalendar
        v-model="model"
        class="w-full h-full md:w-[40rem]"
        @close="toggle(false)"
        @start-date-select="onStartDateSelect"
        @date-range-change="onDateRangeRange"
        @tab-change="onDatePickerTabChange"
        @reset="onReset"
        @apply="onApply"
      />
    </template>
  </RedesignWebccPopupDialog>
</template>

<script setup lang="ts">
import RedesignWebccPopupDialog from '~/components/pages/search/redesign/RedesignWebccPopupDialog.vue'
import type { CalendarEventState } from '~/components/pages/search/redesign/RedesignDateRangeSearchCalendar.vue'
import type { DatePeriod } from '~/utils/dates'

const trPrefix = 'www.components.elements.SearchBox.'

const model = defineModel<DatePeriod | undefined>({ required: true })

const dialogRef = ref<InstanceType<typeof RedesignWebccPopupDialog>>()

defineExpose({
  toggle(value?: boolean | undefined) {
    if (value) dialogRef.value?.toggle()
  },
})

const tracking = useTracking()
const text = useDatesTripText(model)

function onStartDateSelect(
  value: Date | undefined,
  eventState: CalendarEventState,
) {
  if (!value) return
  tracking.handlers?.search.datesArrivalSelect(
    value,
    eventState.tab === 'flexible',
  )
  tracking.handlers?.homepage.datesArrivalSelect(value)
}

function onDateRangeRange(range: DateRange, eventState: CalendarEventState) {
  if (!range.start || !range.end) return

  const duration =
    'duration' in eventState && eventState.duration
      ? eventState.duration
      : calculateDuration(range.start, range.end)

  tracking.handlers?.search.datesDurationSelect(
    duration,
    eventState.tab === 'flexible',
  )
  tracking.handlers?.homepage.datesDurationSelect(duration)
}

function onDatePickerTabChange(tab: CalendarEventState['tab']) {
  tracking.handlers?.search.datesModeSwitch(tab === 'flexible')
}

function onReset(state: CalendarEventState) {
  tracking.handlers?.search.datesClear(state.tab === 'flexible')
}

function onApply(period: DatePeriod | undefined) {
  tracking.handlers?.search.datesApply(isFlexDatePeriod(period))
}

function handleClear() {
  model.value = undefined
}
</script>
